import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useForeePay } from "react-foree-pay-checkout";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import ProgressBar from "../../components/common/ProgressBar";
import PaymentMethods from "../../components/pay/PaymentMethods";
import { callGetWebservice, callPostWebservice } from "../../hooks/api";
import useDeviceDetails from "../../hooks/useDeviceDetails";
import { response as responseKey } from "../../utils/constants";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from "../../utils/localStorageUtils";
import {
  getPaymentObject,
  handleForeeCallback,
  initiateForeePay,
  printLogs,
} from "../../utils/Utilities";

import OrderNotFound from "../../components/OrderNotFound";
import PremiumLoader from "../../components/PremiumLoader";
import "./OrderPayment.css";

const renderOptions = (optionsStr) => {
  if (!optionsStr) return null;
  try {
    const parsed = JSON.parse(optionsStr);
    if (typeof parsed === "object" && parsed !== null) {
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
    }
  } catch (e) {
    return optionsStr;
  }
  return optionsStr;
};

const OrderPayment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const deviceDetails = useDeviceDetails();
  const { executeForeePay } = useForeePay();

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const tokenFromUrl = queryParams.get("token");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merchantSettings, setMerchantSettings] = useState(null);
  const [xPay, setXPayData] = useState(null);
  const [formError, setFormError] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Optionally fetch order details if needed for validation or additional display
    if (tokenFromUrl) {
      let reqData = { type: "order-payment", token: tokenFromUrl };
      printLogs(reqData);
      callGetWebservice(
        "portal-order-details",
        reqData,
        (response) => {
          if (response.success) {
            const fetchedOrder = response.data.order;
            setOrder(fetchedOrder);

            // Fetch Merchant Settings
            callGetWebservice(
              "portal-merchant-settings",
              {
                merchantId: fetchedOrder?.merchant_id,
                outletId: fetchedOrder?.outlet_id,
                type: fetchedOrder?.order_type,
              },
              (response) => {
                setMerchantSettings(response.data);
                setLoading(false);
              },
              null,
              dispatch,
              navigate,
            );
          } else {
            setError(response);
            setLoading(false);
          }
        },
        (error) => {
          setError(error.errMessage);
          setLoading(false);
        },
        null,
        dispatch,
        navigate,
      );
    }
  }, [tokenFromUrl, dispatch, navigate]);

  const formik = useFormik({
    initialValues: {
      payment_id: "",
    },
    validationSchema: Yup.object().shape({
      payment_id: Yup.string().required("Please select a payment method"),
    }),
    onSubmit: (values, { setSubmitting }) => {
      try {
        setFormError(null);
        const paymentObject = getPaymentObject(
          merchantSettings,
          values.payment_id,
        );

        if (!paymentObject) {
          setFormError("Please select a valid payment method");
          setSubmitting(false);
          return;
        }

        const postData = {
          slug: order?.slug,
          order_slug: order?.slug,
          payment_id: paymentObject.payment_id,
          payment_gateway: paymentObject.display_name,
          instance_id: paymentObject.instance_id,
          merchantId: order?.merchant_id,
          user_agent: deviceDetails.userAgent,
          channel: deviceDetails.channel,
          request_type: "Payment",
          browser: deviceDetails.browser,
          device: deviceDetails.device,
          os: deviceDetails.os,
          timezone: merchantSettings?.time_zone,
        };

        callPostWebservice(
          "order-payment",
          postData,
          (response) => {
            setSubmitting(false);
            const data = response.data;

            if (!data.is_order_success) {
              navigateToCompleteOrder(data);
              return;
            }

            if (response.data.has_redirect) {
              if (response.data.key === "foreepay") {
                callForePay(response.data);
              } else if (response.data.key === "card") {
                setXPayData({
                  api_key: response.data.api_key,
                  account_id: response.data.account_id,
                  hmac_secret: response.data.hmac_secret,
                  slug: response.data.slug,
                  order_ref: response.data.order_ref,
                  instance_id: paymentObject.instance_id,
                  order_date: response.data.created_at,
                  order_message: response.data.order_message,
                });
              } else {
                window.location.href = response.data.url;
              }
            } else {
              navigateToCompleteOrder(data);
            }
          },
          (error) => {
            setSubmitting(false);
            setFormError(
              error.errMessage ||
                "Failed to initiate payment. Please try again.",
            );
          },
          dispatch,
          navigate,
        );
      } catch (error) {
        console.error("Payment submission error:", error);
        setFormError("An error occurred. Please try again.");
        setSubmitting(false);
      }
    },
  });

  const callForePay = (data) => {
    initiateForeePay(
      data,
      saveToLocalStorage,
      responseKey,
      executeForeePay,
      foreeCallbackFunc,
    );
  };

  const foreeCallbackFunc = (responseJsonObject = null) => {
    handleForeeCallback(
      responseJsonObject,
      getFromLocalStorage,
      removeFromLocalStorage,
      responseKey,
      callGetWebservice,
      (response, originalResponse) => {
        navigate("/payment-receipt", {
          state: {
            order_id: response.data.order_ref,
            success: response.data.success,
            created_at: response.data.created_at,
            message: originalResponse.message,
            merchantId: response.data.merchant_id,
          },
        });
      },
    );
  };

  const shouldShowXPay =
    formik.values.payment_id &&
    getPaymentObject(merchantSettings, formik.values.payment_id)?.key ===
      "card" &&
    xPay;

  // Filter out payment method with id 1 (Cash on Delivery)
  const filteredMerchantSettings = merchantSettings
    ? {
        ...merchantSettings,
        merchant_integrations: merchantSettings.merchant_integrations.filter(
          (integration) => integration.integration_method_id !== 1,
        ),
      }
    : null;

  const navigateToCompleteOrder = (data) => {
    navigate("/payment-receipt", {
      state: {
        order_id: data.order_ref,
        success: data.is_order_success,
        created_at: data.created_at,
        message: data.order_message,
        merchantId: data.merchant_id,
      },
    });
  };

  if (loading) {
    return <PremiumLoader message="Setting up secure payment..." />;
  }

  return (
    <div className="payment-page-wrapper">
      <div className="payment-particles">
        <div className="payment-particle p-1"></div>
        <div className="payment-particle p-2"></div>
        <div className="payment-particle p-3"></div>
      </div>

      {order ? (
        <div className="payment-card">
          <div className="payment-header">
            {merchantSettings?.logo && (
              <img
                src={merchantSettings.logo}
                alt="Merchant Logo"
                className="merchant-logo"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <h2 className="payment-title mt-3">Order Details</h2>
            <div className="order-badges mt-2 d-flex justify-content-center gap-2">
              <span className="badge badge-status">{order.current_status}</span>
              <span
                className={`badge badge-payment ${order.payment_status?.toLowerCase()}`}
              >
                {order.payment_status}
              </span>
            </div>
          </div>

          <div className="order-body px-4 py-3">
            <div className="info-group d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
              <div className="info-label text-muted text-uppercase">
                Order No
              </div>
              <div className="info-value font-weight-bold">{order.slug}</div>
            </div>

            <div className="items-section mb-4">
              <h4 className="section-title mb-3">
                Items ({order.total_quantity})
              </h4>
              <div className="items-list">
                {order.order_items?.map((item, idx) => (
                  <div
                    key={idx}
                    className="item-row d-flex align-items-center mb-3 p-3 rounded"
                  >
                    <div className="item-image-wrapper me-3">
                      {item.itemable?.image ? (
                        <img
                          src={item.itemable.image}
                          alt={item.itemable.name}
                          className="item-image w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <div className="item-image-placeholder w-100 h-100 d-flex align-items-center justify-content-center bg-light text-muted small">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="item-details flex-grow-1">
                      <div className="item-name font-weight-bold">
                        {item.itemable?.name} x {item.quantity}
                      </div>
                      <div className="item-options text-muted small mt-1">
                        {renderOptions(item.options)}
                      </div>
                    </div>
                    <div className="item-price font-weight-bold">
                      {order.currency?.symbol}{" "}
                      {Number(item.sale_price).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="amount-breakdown p-4 rounded mb-4">
              <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                <span>Subtotal</span>
                <span className="breakdown-value">
                  {order.currency?.symbol}{" "}
                  {Number(order.total_price).toLocaleString()}
                </span>
              </div>

              {order.total_shipment && Number(order.total_shipment) > 0 && (
                <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                  <span>Delivery Fee</span>
                  <span className="breakdown-value">
                    {order.currency?.symbol}{" "}
                    {Number(order.total_shipment).toLocaleString()}
                  </span>
                </div>
              )}

              {order.gst_val && Number(order.gst_val) > 0 && (
                <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                  <span>GST ({Number(order.gst_val)}%)</span>
                  <span className="breakdown-value">
                    {order.currency?.symbol}{" "}
                    {Number(order.gst).toLocaleString()}
                  </span>
                </div>
              )}

              {order.sales_tax_per && Number(order.sales_tax_per) > 0 && (
                <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                  <span>Sales Tax ({Number(order.sales_tax_per)}%)</span>
                  <span className="breakdown-value">
                    {order.currency?.symbol}{" "}
                    {Number(order.sales_tax).toLocaleString()}
                  </span>
                </div>
              )}

              {order.income_tax_per && Number(order.income_tax_per) > 0 && (
                <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                  <span>Income Tax ({Number(order.income_tax_per)}%)</span>
                  <span className="breakdown-value">
                    {order.currency?.symbol}{" "}
                    {Number(order.income_tax).toLocaleString()}
                  </span>
                </div>
              )}

              {order.tip_per && Number(order.tip_per) > 0 && (
                <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                  <span>Tip ({Number(order.tip_per)}%)</span>
                  <span className="breakdown-value">
                    {order.currency?.symbol}{" "}
                    {Number(order.tip).toLocaleString()}
                  </span>
                </div>
              )}

              {order.tip &&
                Number(order.tip) > 0 &&
                (!order.tip_per || Number(order.tip_per) === 0) && (
                  <div className="breakdown-row d-flex justify-content-between mb-3 text-muted">
                    <span>Tip</span>
                    <span className="breakdown-value">
                      {order.currency?.symbol}{" "}
                      {Number(order.tip).toLocaleString()}
                    </span>
                  </div>
                )}

              {order.coupon_val && Number(order.coupon_val) > 0 && (
                <div className="breakdown-row discount d-flex justify-content-between mb-3">
                  <span>Discount</span>
                  <span className="breakdown-value">
                    - {order.currency?.symbol}{" "}
                    {Number(order.coupon_val).toLocaleString()}
                  </span>
                </div>
              )}

              <div className="breakdown-row total d-flex justify-content-between mt-4 pt-3">
                <span>Total Amount</span>
                <span>
                  {order.currency?.symbol}{" "}
                  {Number(order.total_amount).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="payment-body px-4 pb-4 border-top pt-4">
            <form onSubmit={formik.handleSubmit}>
              <h4 className="payment-method-title">Choose payment method</h4>

              <div className="payment-methods-wrapper">
                <PaymentMethods
                  loading={loading}
                  merchantSettings={filteredMerchantSettings}
                  formik={formik}
                  shouldShowXPay={shouldShowXPay}
                  xPay={xPay}
                  fromPaymentPage={true}
                />
              </div>

              {formik.touched.payment_id && formik.errors.payment_id && (
                <div className="payment-validation-error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{formik.errors.payment_id}</span>
                </div>
              )}

              {formError && (
                <div
                  className="alert alert-danger custom-alert mt-4 d-flex align-items-center"
                  role="alert"
                >
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="btn pay-button w-100"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>{" "}
                    Processing Payment...
                  </>
                ) : (
                  `Pay ${order?.currency?.symbol || ""} ${order?.total_amount.toLocaleString()}`
                )}
              </button>

              <div className="secure-badge">
                <i className="fas fa-lock"></i>
                <span>Secured by SSL Encryption</span>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="payment-card">
          <div className="payment-header">
            <img
              src={merchantSettings?.logo || "/assets/images/logo.png"}
              alt="Merchant Logo"
              className="merchant-logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <OrderNotFound error={error} />
        </div>
      )}

      <ProgressBar
        submitting={formik.isSubmitting}
        message="Processing your payment..."
      />
    </div>
  );
};

export default OrderPayment;
